/** Tipo de voz / tesitura para calibrar la detección de pitch. */
export type VoiceTessitura = 'ninos' | 'grave' | 'agudo';

export interface VoiceTessituraProfile {
  id: VoiceTessitura;
  title: string;
  description: string;
  emoji: string;
  /** Hz mínimo esperado (fundamental). */
  minHz: number;
  /** Hz máximo esperado. */
  maxHz: number;
  /** Centro preferido para desambiguar octavas YIN (si preferFundamental es false). */
  hzCenter: number;
  /** Preferir fundamental frente a armónicos (voz grave). */
  preferFundamental: boolean;
  /** Semitonos sumados al MIDI detectado antes de emparejar con el juego. */
  midiTranspose: number;
  highpassHz: number;
  lowpassHz: number;
  gain: number;
}

export const VOICE_TESSITURAS: VoiceTessituraProfile[] = [
  {
    id: 'ninos',
    title: 'Niños',
    description: 'Voz infantil (La3–Do5)',
    emoji: '🧒',
    minHz: 110,
    maxHz: 750,
    hzCenter: 330,
    preferFundamental: false,
    midiTranspose: 0,
    highpassHz: 55,
    lowpassHz: 2800,
    gain: 3.5,
  },
  {
    id: 'grave',
    title: 'Grave',
    description: 'Voz baja / adulto (una octava abajo)',
    emoji: '🧔',
    minHz: 75,
    maxHz: 400,
    hzCenter: 175,
    preferFundamental: true,
    /** Reservado; la octava se pliega con foldMidiToSingRange (La3–Do5). */
    midiTranspose: 12,
    highpassHz: 45,
    lowpassHz: 2200,
    gain: 4.0,
  },
  {
    id: 'agudo',
    title: 'Agudo',
    description: 'Voz alta / niñas (más brillante)',
    emoji: '👧',
    minHz: 180,
    maxHz: 950,
    hzCenter: 440,
    preferFundamental: false,
    midiTranspose: 0,
    highpassHz: 70,
    lowpassHz: 3200,
    gain: 3.2,
  },
];

const STORAGE_KEY = 'cling-voice-tessitura';

export function getVoiceProfile(id: VoiceTessitura): VoiceTessituraProfile {
  return VOICE_TESSITURAS.find((v) => v.id === id) ?? VOICE_TESSITURAS[0]!;
}

export function loadVoiceTessitura(): VoiceTessitura {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'ninos' || raw === 'grave' || raw === 'agudo') return raw;
  } catch {
    /* ignore */
  }
  return 'ninos';
}

export function saveVoiceTessitura(id: VoiceTessitura): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
}

/** MIDI del juego a partir del MIDI crudo del micrófono. */
export { gameMidiFromDetected } from './juniorPitchStabilizer';

/** Perfil DSP para createVocalAudioChain. */
export function voiceToDetectProfile(profile: VoiceTessituraProfile) {
  return {
    minHz: profile.minHz,
    maxHz: profile.maxHz,
    hzCenter: profile.hzCenter,
    preferFundamental: profile.preferFundamental,
    highpassHz: profile.highpassHz,
    lowpassHz: profile.lowpassHz,
    gain: profile.gain,
  };
}
